import * as React from 'react'
import { Copy, RefreshCw } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DetailPage } from '@/components/templates/detail-page'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { getJSON, patchJSON } from '@/lib/api'
import { displayDateTime } from '@/lib/dates'
import { InvoiceDiscountCodeCard, type InvoiceDiscountCode } from '@/pages/InvoiceDiscountCodeCard'
import { AccountingSyncSummary, InvoicePageState } from '@/pages/InvoiceDetailPageSummary'
import { InvoiceLineItemsCard } from '@/pages/InvoiceLineItemsCard'
import { InvoiceDatesCard } from '@/pages/InvoiceDatesCard'
import { InvoiceDetailsCard } from '@/pages/InvoiceDetailsCard'
import { InvoiceHeaderActions, useInvoiceChargeNow } from '@/pages/InvoiceHeaderActions'
import { invoiceStatusVariant } from '@/pages/InvoicesPage.status'
import { RemoveInvoiceDiscountDialog } from '@/pages/RemoveInvoiceDiscountDialog'
import { useInvoiceDiscountCodes } from '@/pages/useInvoiceDiscountCodes'
import { VoidInvoiceDialog, type VoidInvoiceResponse } from '@/pages/VoidInvoiceDialog'
import {
  isConnectionOwned,
  OwnedInvoiceBanner,
  OwnedInvoiceHeaderActions,
} from '@/pages/OwnedInvoiceProvenance'
import {
  RecordPaymentDialog,
  submitPaymentForm,
  submitPhonePaymentForm,
} from '@/pages/RecordPaymentDialog'
import type { Invoice, InvoiceResponse } from '@/pages/InvoicesPage.types'
import type { PaymentForm, PhonePaymentForm } from '@/pages/InvoicesPage.types'
import { useViewOnly } from '@/components/templates/app-shell'

interface InvoiceDetailPageProps {
  invoiceId: number
  currentAssociationDate?: string
  onBack: () => void
}

// The detail page owns the invoice mutations and their dialogs.
// eslint-disable-next-line max-lines-per-function, complexity
export function InvoiceDetailPage({
  invoiceId,
  currentAssociationDate,
  onBack,
}: InvoiceDetailPageProps) {
  const queryClient = useQueryClient()
  const viewOnly = useViewOnly()
  const [paymentDialogOpen, setPaymentDialogOpen] = React.useState(false)
  const [voidDialogOpen, setVoidDialogOpen] = React.useState(false)
  const [voidConfirmation, setVoidConfirmation] = React.useState<string | null>(null)
  const invoiceQuery = useInvoice(invoiceId)
  const recordPayment = useMutation({
    mutationFn: (form: PaymentForm) =>
      submitPaymentForm(invoiceQuery.data!.invoice.contact_id, form),
    onSuccess: () =>
      finishRecordedPayment(queryClient, invoiceId, () => setPaymentDialogOpen(false)),
  })
  const recordPhonePayment = useMutation({
    mutationFn: (form: PhonePaymentForm) => submitPhonePaymentForm(invoiceId, form),
    onSuccess: async (data) => {
      queryClient.setQueryData(['invoice', invoiceId], data)
      await queryClient.invalidateQueries({ queryKey: ['invoices'] })
      setPaymentDialogOpen(false)
    },
  })
  const regeneratePaymentLink = useMutation({
    mutationFn: () =>
      patchJSON<InvoiceResponse>(`/api/v1/invoices/${invoiceId}/regenerate_payment_link`, {}),
    onSuccess: async (data) => {
      queryClient.setQueryData(['invoice', invoiceId], data)
      await queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
  const voidInvoice = useMutation({
    mutationFn: (refundToPayer: boolean) =>
      patchJSON<VoidInvoiceResponse>(`/api/v1/invoices/${invoiceId}/void`, {
        refund_to_payer: refundToPayer,
      }),
    onSuccess: async (data) => {
      queryClient.setQueryData(['invoice', invoiceId], { invoice: data.invoice })
      await queryClient.invalidateQueries({ queryKey: ['invoices'] })
      setVoidConfirmation(data.void_confirmation.message)
      setVoidDialogOpen(false)
    },
  })
  const chargeNow = useInvoiceChargeNow(invoiceId)
  const invoice = invoiceQuery.data?.invoice
  const owned = isConnectionOwned(invoice)
  const discountCodes = useInvoiceDiscountCodes(invoiceId, invoice)

  if (invoiceQuery.isLoading || invoiceQuery.isError || !invoice)
    return <InvoicePageState loading={invoiceQuery.isLoading} onBack={onBack} />

  return (
    <DetailPage
      title={`Invoice #${invoice.number}`}
      subtitle={invoice.contact.name}
      badge={<Badge variant={invoiceStatusVariant[invoice.status]}>{invoice.status}</Badge>}
      actions={
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <Button asChild variant="outline" size="sm" className="shrink-0 whitespace-nowrap">
            <a href={`/finance/invoices/${invoice.id}/print`} target="_blank" rel="noreferrer">
              Print / save as PDF
            </a>
          </Button>
          {!viewOnly && !owned && (
            <InvoiceHeaderActions
              invoice={invoice}
              chargingNow={chargeNow.pending}
              onChargeNow={chargeNow.charge}
              onRecordPayment={() => setPaymentDialogOpen(true)}
              onVoid={() => setVoidDialogOpen(true)}
            />
          )}
          {!viewOnly && owned && (
            <OwnedInvoiceHeaderActions
              invoice={invoice}
              onRecordPayment={() => setPaymentDialogOpen(true)}
            />
          )}
        </div>
      }
      backHref="#"
      backLabel="Invoices"
      onBack={onBack}
    >
      {voidConfirmation && <Alert>{voidConfirmation}</Alert>}
      {chargeNow.message && <Alert>{chargeNow.message}</Alert>}
      {chargeNow.error && <Alert>Saved Card charge could not be completed.</Alert>}
      {owned && <OwnedInvoiceBanner invoice={invoice} />}
      <AccountingSyncSummary invoice={invoice} />
      <InvoiceDetail
        invoice={invoice}
        viewOnly={viewOnly || owned}
        discountCodes={discountCodes.codes}
        discountCodesLoading={discountCodes.loading}
        discountCodesError={discountCodes.failed}
        selectedDiscountCode={discountCodes.selectedCode}
        discountChangePending={discountCodes.pending}
        discountChangeError={discountCodes.error}
        onSelectDiscountCode={discountCodes.setSelectedCode}
        onApplyDiscountCode={viewOnly ? () => undefined : discountCodes.apply}
        onRemoveDiscountCode={viewOnly ? () => undefined : discountCodes.openRemoveDialog}
        regeneratingPaymentLink={regeneratePaymentLink.isPending}
        regeneratePaymentLinkError={regeneratePaymentLink.isError}
        onRegeneratePaymentLink={viewOnly ? () => undefined : () => regeneratePaymentLink.mutate()}
      />
      {paymentDialogOpen && (
        <RecordPaymentDialog
          invoice={invoice}
          currentAssociationDate={currentAssociationDate}
          pending={recordPayment.isPending}
          error={recordPayment.error}
          phonePending={recordPhonePayment.isPending}
          phoneError={recordPhonePayment.error}
          onClose={() => setPaymentDialogOpen(false)}
          onSubmit={(form) => recordPayment.mutate(form)}
          onSubmitPhone={(form) => recordPhonePayment.mutate(form)}
        />
      )}
      {voidDialogOpen && (
        <VoidInvoiceDialog
          pending={voidInvoice.isPending}
          error={voidInvoice.isError}
          onClose={() => setVoidDialogOpen(false)}
          onSubmit={(refundToPayer) => voidInvoice.mutate(refundToPayer)}
        />
      )}
      {discountCodes.removeDialogOpen && (
        <RemoveInvoiceDiscountDialog
          pending={discountCodes.removePending}
          error={discountCodes.removeFailed}
          onClose={discountCodes.closeRemoveDialog}
          onSubmit={discountCodes.remove}
        />
      )}
    </DetailPage>
  )
}

async function finishRecordedPayment(
  queryClient: ReturnType<typeof useQueryClient>,
  invoiceId: number,
  close: () => void,
) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] }),
    queryClient.invalidateQueries({ queryKey: ['invoices'] }),
  ])
  close()
}

function useInvoice(invoiceId: number) {
  return useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => getJSON<InvoiceResponse>(`/api/v1/invoices/${invoiceId}`),
    retry: false,
  })
}

function InvoiceDetail({
  invoice,
  viewOnly,
  discountCodes,
  discountCodesLoading,
  discountCodesError,
  selectedDiscountCode,
  discountChangePending,
  discountChangeError,
  onSelectDiscountCode,
  onApplyDiscountCode,
  onRemoveDiscountCode,
  regeneratingPaymentLink,
  regeneratePaymentLinkError,
  onRegeneratePaymentLink,
}: {
  invoice: Invoice
  viewOnly: boolean
  discountCodes: InvoiceDiscountCode[]
  discountCodesLoading: boolean
  discountCodesError: boolean
  selectedDiscountCode: string
  discountChangePending: boolean
  discountChangeError: Error | null
  onSelectDiscountCode: (code: string) => void
  onApplyDiscountCode: () => void
  onRemoveDiscountCode: () => void
  regeneratingPaymentLink: boolean
  regeneratePaymentLinkError: boolean
  onRegeneratePaymentLink: () => void
}) {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <InvoiceLineItemsCard invoice={invoice} />

        <aside>
          <Card>
            <CardContent
              data-testid="invoice-sidebar-sections"
              className="divide-y divide-border p-0"
            >
              <InvoiceDetailsCard invoice={invoice} />
              {!viewOnly && (invoice.discount_code_change_available || invoice.discount_row) && (
                <InvoiceDiscountCodeCard
                  invoice={invoice}
                  codes={discountCodes}
                  loading={discountCodesLoading}
                  failed={discountCodesError}
                  selectedCode={selectedDiscountCode}
                  pending={discountChangePending}
                  error={discountChangeError}
                  onSelect={onSelectDiscountCode}
                  onApply={onApplyDiscountCode}
                  onRemove={onRemoveDiscountCode}
                />
              )}
              {invoice.payment_link_url ? (
                <PaymentLinkCard
                  url={invoice.payment_link_url}
                  allowRegenerate={!viewOnly}
                  pending={regeneratingPaymentLink}
                  error={regeneratePaymentLinkError}
                  onRegenerate={onRegeneratePaymentLink}
                />
              ) : (
                invoice.payment_link_unavailable_reason && (
                  <PaymentLinkUnavailableCard reason={invoice.payment_link_unavailable_reason} />
                )
              )}
              <InvoiceDatesCard invoice={invoice} allowEdit={!viewOnly} />
              <section className="space-y-3 px-4 py-3">
                <h2 className="text-body font-semibold text-foreground">History</h2>
                <div className="space-y-3">
                  {invoice.history.map((entry) => (
                    <div key={entry.id} className="text-body-sm">
                      <p className="text-foreground">{entry.event}</p>
                      {entry.amount && (
                        <p className="text-caption text-muted-foreground">
                          {entry.amount}
                          {entry.payment_method ? ` · ${entry.payment_method}` : ''}
                          {entry.reference ? ` · ${entry.reference}` : ''}
                          {entry.balance ? ` · Balance ${entry.balance}` : ''}
                        </p>
                      )}
                      <p className="text-caption text-muted-foreground">
                        {entry.actor ?? '-'} · {displayDateTime(entry.occurred_at)}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  )
}

function PaymentLinkCard({
  url,
  allowRegenerate,
  pending,
  error,
  onRegenerate,
}: {
  url: string
  allowRegenerate: boolean
  pending: boolean
  error: boolean
  onRegenerate: () => void
}) {
  const [copied, setCopied] = React.useState(false)

  const copyLink = async () => {
    await navigator.clipboard.writeText(url)
    setCopied(true)
  }

  const regenerate = () => {
    if (!window.confirm('Regenerate this Payment Link? The old URL will stop working.')) return

    onRegenerate()
  }

  return (
    <section className="space-y-2 px-4 py-3">
      <h2 className="text-body font-semibold text-foreground">Payment Link</h2>
      <Label htmlFor="invoice-payment-link">Payment Link</Label>
      <div className="flex gap-2">
        <Input id="invoice-payment-link" aria-label="Payment Link" value={url} readOnly />
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-10 w-10 px-0"
          aria-label="Copy Payment Link"
          title="Copy Payment Link"
          onClick={() => void copyLink()}
        >
          <Copy className="h-4 w-4" aria-hidden="true" strokeWidth={1.5} />
        </Button>
        {allowRegenerate && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-10 w-10 px-0"
            aria-label="Regenerate Payment Link"
            title="Regenerate Payment Link"
            disabled={pending}
            onClick={regenerate}
          >
            <RefreshCw className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        )}
      </div>
      {copied && <p className="text-caption text-muted-foreground">Copied</p>}
      {error && <Alert>Payment Link could not be regenerated</Alert>}
    </section>
  )
}

function PaymentLinkUnavailableCard({ reason }: { reason: string }) {
  return (
    <section className="space-y-2 px-4 py-3">
      <h2 className="text-body font-semibold text-foreground">Payment Link</h2>
      <p className="text-body-sm text-muted-foreground">{reason}</p>
    </section>
  )
}
